import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { config } from "../../config";
import { calculateFinalPrice, formatPrice } from "../../utils/pricing";

function StayCard({ stay }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handleViewRoomOptions = () => {
    window.open(`/hotels/hotelInformation?searchResultId=${stay.id}`, "_blank");
  };

  const calculatePricePerNight = () => {
    const checkIn = new Date(stay.check_in_date);
    const checkOut = new Date(stay.check_out_date);
    const numberOfNights = Math.ceil(
      (checkOut - checkIn) / (1000 * 60 * 60 * 24)
    );
    const totalPrice = calculateFinalPrice(
      stay.cheapest_rate_total_amount,
      true
    );
    return (totalPrice / numberOfNights).toFixed(2);
  };

  const nextImage = (e) => {
    e.stopPropagation(); // Prevent card click
    setCurrentImageIndex((prev) =>
      prev === stay.accommodation.photos.length - 1 ? 0 : prev + 1
    );
  };

  const previousImage = (e) => {
    e.stopPropagation(); // Prevent card click
    setCurrentImageIndex((prev) =>
      prev === 0 ? stay.accommodation.photos.length - 1 : prev - 1
    );
  };

  return (
    <div
      onClick={handleViewRoomOptions}
      className="bg-white cursor-pointer rounded-lg mb-4 shadow-none border-gray-200 border overflow-hidden h-[250px]"
    >
      <div className="flex h-full">
        <div className="relative w-1/3 min-w-[33.333%] h-full group">
          <img
            src={stay.accommodation.photos[currentImageIndex]?.url}
            alt={`${stay.accommodation.name}`}
            className="w-full h-full object-cover"
          />

          {stay.accommodation.photos.length > 1 && (
            <>
              <button
                onClick={previousImage}
                className="absolute hidden md:block lg:block h-10 w-10 left-2 top-1/2 -translate-y-1/2 bg-gray-700 hover:bg-black p-2 rounded-full opacity-90 group-hover:opacity-100 transition-opacity"
                aria-label="Previous image"
              >
                <FontAwesomeIcon
                  icon={faChevronLeft}
                  className="text-gray-100"
                />
              </button>

              <button
                onClick={nextImage}
                className="absolute hidden md:block lg:block right-2 h-10 w-10 top-1/2 -translate-y-1/2 bg-gray-700 hover:bg-black p-2 rounded-full opacity-90 group-hover:opacity-100 transition-opacity"
                aria-label="Next image"
              >
                <FontAwesomeIcon
                  icon={faChevronRight}
                  className="text-gray-100"
                />
              </button>

              <div className="absolute bottom-2 right-2 bg-black/60 text-white px-2 py-1 rounded-full text-sm">
                {currentImageIndex + 1}/{stay.accommodation.photos.length}
              </div>
            </>
          )}
        </div>

        <div className="w-2/3 flex flex-col md:flex-row p-4 md:p-6 overflow-hidden">
          {/* Hotel Details */}
          <div className="md:w-4/6 border-r border-r-2 mr-2 flex-grow lg:w-4/6 overflow-hidden">
            <h3 className="text-xl font-semibold truncate">
              {stay.accommodation.name}
            </h3>
            <p className="text-gray-600 mt-2 truncate">
              {stay.accommodation.location.address.city_name},{" "}
              {stay.accommodation.location.address.country_code}
            </p>
            <div className="flex items-center my-2">
              {[...Array(stay.accommodation.rating)].map((_, i) => (
                <FontAwesomeIcon
                  key={i}
                  icon={faStar}
                  className="text-yellow-400"
                />
              ))}
            </div>
            {stay.accommodation.review_score && (
              <span
                className={`font-semibold ${config.primaryColorBg} p-2 text-sm rounded-lg text-gray-800`}
              >
                {stay.accommodation.review_score}
              </span>
            )}
            <div className="mt-2 hidden md:block lg:block">
              <p className="text-gray-700 mb-2">This property offers:</p>
              <div className="flex flex-wrap gap-2 overflow-hidden max-h-[40px]">
                {stay.accommodation.amenities
                  .slice(0, 3)
                  .map((amenity, index) => (
                    <span
                      key={index}
                      className="inline-block px-3 py-1 text-sm text-gray-600 bg-gray-100 border border-gray-200 rounded-md"
                    >
                      {amenity.description}
                    </span>
                  ))}
              </div>
            </div>
          </div>

          {/* Price */}
          <div className="flex flex-col w-fit items-end justify-between h-full">
            <div>
              <p className="text-xl flex flex-col font-bold">
                {formatPrice(
                  calculateFinalPrice(stay.cheapest_rate_total_amount, true),
                  "USD" // You might want to get the currency from the API
                )}
                <span className="font-normal text-xs">
                  {formatPrice(calculatePricePerNight(), "USD")} per night
                </span>
                <span className="text-xs text-gray-500 font-light block">
                  {stay.rooms} {stay.rooms === 1 ? "room" : "rooms"}
                </span>
              </p>
            </div>

            <button
              className={`${config.primaryColorBg} text-xs w-fit text-gray-800 px-4 py-2 rounded-md mt-auto hover:text-black`}
              onClick={handleViewRoomOptions}
            >
              See availability
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StayCard;
